import { api, type CommunityResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';
import type { InteractionClassification } from '@ferrari/types';

/**
 * Comunidade e moderação.
 *
 * A tela existe para tornar a ocultação **revisável**. Um comentário escondido
 * que simplesmente some da lista é indistinguível de um comentário que nunca
 * existiu — e é assim que crítica legítima desaparece sem ninguém notar.
 *
 * Por isso o escondido continua na lista, marcado, com o motivo e a política que
 * o escondeu. Quem revisa consegue discordar; sem isso, não há o que revisar.
 */

const CLASS_LABEL: Record<InteractionClassification, string> = {
  unclassified: 'Sem classificação',
  normal: 'Normal',
  question: 'Pergunta',
  praise: 'Elogio',
  confusion: 'Confusão',
  support_request: 'Pedido de suporte',
  commercial_intent: 'Intenção comercial',
  constructive_criticism: 'Crítica construtiva',
  professional_disagreement: 'Discordância profissional',
  negative_feedback: 'Feedback negativo',
  hostile: 'Hostil',
  spam: 'Spam',
  scam: 'Golpe',
  personal_attack: 'Ataque pessoal',
  repeated_hostility: 'Hostilidade repetida',
  threat: 'Ameaça',
  hate: 'Ódio',
  impersonation: 'Falsidade ideológica',
  customer_complaint: 'Reclamação de cliente',
  illegal_content: 'Conteúdo ilegal',
  platform_material_violation: 'Violação das regras da plataforma',
};

const RISK_LABEL: Record<string, string> = {
  unknown: 'Não avaliado',
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
  exceptional: 'Excepcional',
};

const VISIBILITY_LABEL: Record<string, string> = {
  visible: 'Visível',
  hidden: 'Escondido',
  hidden_by_policy: 'Escondido por política',
  deleted_by_author: 'Apagado pelo autor',
};

const RESPONSE_STATE_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  awaiting_human: 'Esperando pessoa',
  approved: 'Aprovada',
  sent: 'Enviada',
  rejected: 'Recusada',
  superseded: 'Substituída',
};

export function Community() {
  const resource = useResource<CommunityResponse>(() => api.community(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Comunidade e moderação</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Comunidade e moderação</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar as interações.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Isto <strong>não</strong> significa que não há resposta esperando revisão. Significa que
            não sabemos. Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Comunidade e moderação</h1>
        <p className="mt-1 text-ink-muted">
          {formatNumber(data.total)} interação(ões) recentes · {formatNumber(data.hidden_count)}{' '}
          escondida(s) · {formatNumber(data.awaiting_human_count)} resposta(s) esperando pessoa.
        </p>
      </header>

      {data.total === 0 ? (
        <p className="mt-6 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhuma interação registrada. Esta caixa reflete o domínio: vazia significa vazia.
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.interactions.map((i) => (
            <li
              key={i.interaction_id}
              className={`rounded-card border border-line bg-card p-4 shadow-card ${
                i.visibility === 'visible' ? '' : 'border-l-4 border-l-warn bg-warn-surface/40'
              }`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{i.author_handle}</span>
                <span className="text-xs uppercase tracking-wide text-ink-muted">
                  {CLASS_LABEL[i.classification as InteractionClassification] ?? i.classification} ·{' '}
                  {VISIBILITY_LABEL[i.visibility] ?? i.visibility}
                </span>
              </div>

              {/*
                O texto do comentário aparece MESMO quando escondido. Sumir da
                lista é o que torna a ocultação irrevisável.
              */}
              <p className="mt-2">{i.text}</p>

              {i.visibility !== 'visible' ? (
                <p className="mt-2 text-xs text-warn-ink">
                  {i.visibility_reason ?? 'Escondido sem motivo registrado.'}
                  {i.visibility_policy_ref ? ` (${i.visibility_policy_ref})` : ''}
                </p>
              ) : null}

              <p className="mt-2 text-xs text-ink-muted">
                Risco: {RISK_LABEL[i.risk_level] ?? i.risk_level}
                {i.risk_triggers.length > 0 ? ` · gatilhos: ${i.risk_triggers.join(', ')}` : ''}
              </p>

              {i.response ? (
                <div className="mt-3 rounded border border-line bg-surface-muted p-2">
                  <p className="text-xs font-medium">
                    Resposta: {RESPONSE_STATE_LABEL[i.response.state] ?? i.response.state}
                  </p>
                  <p className="mt-1 text-xs">{i.response.sent_text ?? i.response.draft_text}</p>
                  <p className="mt-1 text-xs text-ink-faint">
                    {i.response.gate_reason_code}: {i.response.gate_reason}
                  </p>
                  {i.response.sent_at ? (
                    <p className="mt-1 text-xs text-ink-faint">
                      Enviada em {formatInstant(i.response.sent_at)}. O texto que a pessoa recebeu
                      não se reescreve.
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="mt-2 text-xs text-ink-faint">Nenhuma resposta rascunhada.</p>
              )}

              <p className="mt-2 text-xs text-ink-faint">
                {i.surface} · {formatInstant(i.received_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
